import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Upload, Download, Trash2, Pencil, Users, FileText, ShieldCheck, LogOut, KeyRound } from "lucide-react";

const inputCls = "w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none";
const labelCls = "block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2";

export default function Workspace() {
  const { user, loading, logout } = useAuth();
  const [workspaces, setWorkspaces] = useState(null);
  const [ws, setWs] = useState(null);
  const [records, setRecords] = useState([]);
  const [files, setFiles] = useState([]);
  const [members, setMembers] = useState([]);
  const [recForm, setRecForm] = useState({ title: "", body: "" });
  const [editingId, setEditingId] = useState(null);
  const [invite, setInvite] = useState({ email: "", role: "client" });
  const [inviteToken, setInviteToken] = useState("");
  const [acceptToken, setAcceptToken] = useState("");
  const [busy, setBusy] = useState(false);

  const loadWs = useCallback(async (wid) => {
    const [d, r, f, m] = await Promise.all([
      api.get(`/workspaces/${wid}`), api.get(`/workspaces/${wid}/records`),
      api.get(`/workspaces/${wid}/files`), api.get(`/workspaces/${wid}/members`),
    ]);
    setWs(d.data); setRecords(r.data.records); setFiles(f.data.files); setMembers(m.data.members);
  }, []);

  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data.workspaces);
      if (data.workspaces.length) await loadWs(data.workspaces[0].workspace_id);
    } catch { setWorkspaces([]); }
  }, [loadWs]);

  useEffect(() => { if (user) loadMine(); }, [user, loadMine]);

  if (loading || (user && workspaces === null) || (workspaces?.length > 0 && !ws))
    return <div className="min-h-screen bg-navy grid place-items-center"><Loader2 className="text-safety animate-spin" size={36} /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const accept = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/workspaces/accept", { token: acceptToken.trim() });
      toast.success("Invitation accepted — welcome to your workspace");
      setAcceptToken(""); await loadMine();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not accept invitation"); }
    finally { setBusy(false); }
  };

  const saveRecord = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/workspaces/${ws.workspace_id}/records/${editingId}`, recForm);
        toast.success("Record updated");
      } else {
        await api.post(`/workspaces/${ws.workspace_id}/records`, recForm);
        toast.success("Record added");
      }
      setRecForm({ title: "", body: "" }); setEditingId(null); await loadWs(ws.workspace_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const deleteRecord = async (rid) => {
    try {
      await api.delete(`/workspaces/${ws.workspace_id}/records/${rid}`);
      toast.success("Record deleted"); await loadWs(ws.workspace_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/workspaces/${ws.workspace_id}/files`, fd);
      toast.success("File uploaded"); await loadWs(ws.workspace_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/workspaces/${ws.workspace_id}/files/${f.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const sendInvite = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws.workspace_id}/invitations`, invite);
      setInviteToken(data.token); setInvite({ email: "", role: "client" });
      toast.success("Invitation token created — deliver it manually");
    } catch (err) { toast.error(err.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };

  const toggleMember = async (m) => {
    try {
      await api.patch(`/workspaces/${ws.workspace_id}/members/${m.membership_id}`,
        { status: m.status === "active" ? "disabled" : "active" });
      await loadWs(ws.workspace_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  const myRole = ws?.my_role || "";
  const canWrite = myRole === "owner" || myRole === "consultant";
  const canManage = myRole === "owner";

  // ---- no workspace yet: accept an invitation token (delivered out-of-band) ----
  if (!workspaces?.length) {
    return (
      <div data-testid="workspace-accept" className="min-h-screen bg-navy blueprint-grid grain pt-24 px-6">
        <div className="max-w-md mx-auto bg-charcoal border border-white/10 p-8">
          <KeyRound size={28} className="text-safety mb-4" />
          <h1 className="font-display text-2xl font-bold text-white">Join your workspace</h1>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">
            Paste the invitation token you received directly from your workspace owner.
            Tokens are single-use, expire after 72 hours, and only work for the email they were issued to.
          </p>
          <form onSubmit={accept} className="mt-6 space-y-4">
            <input data-testid="workspace-accept-token" value={acceptToken} onChange={(e) => setAcceptToken(e.target.value)}
              placeholder="Invitation token" className={inputCls} required minLength={16} />
            <button data-testid="workspace-accept-submit" disabled={busy}
              className="w-full bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold py-3 transition-colors">
              {busy ? "Accepting…" : "Accept invitation"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="workspace-home" className="min-h-screen bg-navy text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Private workspace</p>
          <h1 className="font-display text-xl font-bold" data-testid="workspace-name">{ws.name}</h1>
        </div>
        <div className="flex items-center gap-4">
          <span data-testid="workspace-role" className="font-mono text-[11px] uppercase tracking-[0.15em] text-safety border border-safety/40 px-2.5 py-1">{myRole}</span>
          <button data-testid="workspace-logout" onClick={logout} className="text-slate-400 hover:text-white transition-colors" title="Sign out"><LogOut size={18} /></button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-2 gap-6">
        {/* Records */}
        <section className="bg-charcoal border border-white/10 p-6" data-testid="workspace-records">
          <h2 className="font-display font-bold flex items-center gap-2"><FileText size={16} className="text-safety" /> Records</h2>
          {canWrite && (
            <form onSubmit={saveRecord} className="mt-4 space-y-3">
              <input data-testid="record-title" value={recForm.title} onChange={(e) => setRecForm({ ...recForm, title: e.target.value })}
                placeholder="Record title" className={inputCls} required minLength={2} />
              <textarea data-testid="record-body" value={recForm.body} onChange={(e) => setRecForm({ ...recForm, body: e.target.value })}
                placeholder="Details" rows={3} className={inputCls + " resize-y"} />
              <button data-testid="record-save" disabled={busy}
                className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                {editingId ? "Update record" : "Add record"}
              </button>
              {editingId && <button type="button" onClick={() => { setEditingId(null); setRecForm({ title: "", body: "" }); }}
                className="ml-3 text-slate-400 text-sm hover:text-white">Cancel</button>}
            </form>
          )}
          <ul className="mt-5 space-y-3">
            {records.map((r) => (
              <li key={r.record_id} className="border border-white/10 p-4" data-testid={`record-${r.record_id.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">{r.title}</p>
                    {r.body && <p className="text-slate-400 text-sm mt-1 whitespace-pre-wrap">{r.body}</p>}
                  </div>
                  {canWrite && (
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`record-edit-${r.record_id.slice(0, 8)}`} title="Edit"
                        onClick={() => { setEditingId(r.record_id); setRecForm({ title: r.title, body: r.body }); }}
                        className="text-slate-400 hover:text-white"><Pencil size={15} /></button>
                      {canManage && (
                        <button data-testid={`record-delete-${r.record_id.slice(0, 8)}`} title="Delete"
                          onClick={() => deleteRecord(r.record_id)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
            {!records.length && <p className="text-slate-500 text-sm">No records yet.</p>}
          </ul>
        </section>

        <div className="space-y-6">
          {/* Files */}
          <section className="bg-charcoal border border-white/10 p-6" data-testid="workspace-files">
            <h2 className="font-display font-bold flex items-center gap-2"><Upload size={16} className="text-safety" /> Documents</h2>
            {canWrite && (
              <label className="mt-4 flex items-center gap-3 border border-dashed border-white/20 px-4 py-3 cursor-pointer hover:border-safety transition-colors">
                <Upload size={16} className="text-slate-400" />
                <span className="text-sm text-slate-300">Upload a file (pdf, txt, png, jpg, csv, md — max 5 MB)</span>
                <input data-testid="file-upload" type="file" className="hidden" onChange={upload}
                  accept=".pdf,.txt,.png,.jpg,.jpeg,.csv,.md" />
              </label>
            )}
            <ul className="mt-4 space-y-2">
              {files.map((f) => (
                <li key={f.file_id} className="flex items-center justify-between text-sm border border-white/10 px-3.5 py-2.5">
                  <span className="text-slate-300 truncate">{f.filename}</span>
                  <button data-testid={`file-download-${f.file_id.slice(0, 8)}`} onClick={() => download(f)}
                    className="text-safety hover:text-safety-hover shrink-0 ml-3" title="Download"><Download size={15} /></button>
                </li>
              ))}
              {!files.length && <p className="text-slate-500 text-sm">No documents yet.</p>}
            </ul>
          </section>

          {/* Members */}
          <section className="bg-charcoal border border-white/10 p-6" data-testid="workspace-members">
            <h2 className="font-display font-bold flex items-center gap-2"><Users size={16} className="text-safety" /> Members</h2>
            <ul className="mt-4 space-y-2">
              {members.map((m) => (
                <li key={m.membership_id} className="flex items-center justify-between text-sm border border-white/10 px-3.5 py-2.5">
                  <span className="text-slate-300 truncate">{m.email}</span>
                  <span className="flex items-center gap-3 shrink-0 ml-3">
                    <span className={`font-mono text-[10px] uppercase tracking-wider ${m.status === "active" ? "text-safety" : "text-slate-500"}`}>
                      {m.role}{m.status !== "active" ? " · disabled" : ""}
                    </span>
                    {canManage && m.user_id !== user.user_id && (
                      <button data-testid={`member-toggle-${m.membership_id.slice(0, 8)}`} onClick={() => toggleMember(m)}
                        className="text-slate-400 hover:text-white text-xs underline underline-offset-2">
                        {m.status === "active" ? "Disable" : "Enable"}
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {canManage && (
              <form onSubmit={sendInvite} className="mt-5 border-t border-white/10 pt-5 space-y-3">
                <p className={labelCls}>Invite a member (manual delivery)</p>
                <input data-testid="invite-email" type="email" value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="their@email.com" className={inputCls} required />
                <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                  className={inputCls}>
                  <option value="client">Client — read only</option>
                  <option value="consultant">Consultant — read & write</option>
                  <option value="owner">Owner — full control</option>
                </select>
                <button data-testid="invite-create" disabled={busy}
                  className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                  Create invitation token
                </button>
                {inviteToken && (
                  <div className="bg-safety/10 border border-safety/30 p-4 flex items-start gap-3" data-testid="invite-token-box">
                    <ShieldCheck size={16} className="text-safety shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-300 leading-relaxed">No email was sent. Copy this token and deliver it yourself (text, call, in person). Single-use · 72 hours · bound to that email.</p>
                      <p className="font-mono text-xs text-safety break-all mt-2" data-testid="invite-token">{inviteToken}</p>
                      <button type="button" onClick={() => copy(inviteToken)} data-testid="invite-copy"
                        className="mt-2 text-xs text-white flex items-center gap-1.5 hover:text-safety"><Copy size={12} /> Copy token</button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
